import { useState, useEffect, useCallback } from "react";
import {
  fetchAllergensForAdmin,
  saveAllergensToApi,
} from "../../utils/contentApi";
import "./allergensAdmin.css";

const ALLERGEN_COLORS = [
  "#FFD600",
  "#C5E1A5",
  "#FFA726",
  "#A5D6A7",
  "#90CAF9",
  "#FFCC80",
  "#FFF176",
  "#CE93D8",
  "#BDBDBD",
  "#FF8A65",
  "#388E3C",
  "#E0E0E0",
  "#FFAB91",
  "#66BB6A",
];

function AllergensAdmin() {
  const [dishes, setDishes] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [newDishName, setNewDishName] = useState("");
  const [newDishOrigin, setNewDishOrigin] = useState("");
  const [newDishSuppliers, setNewDishSuppliers] = useState("");

  const parseSuppliers = (value) =>
    [...new Set(String(value || "")
      .split(/[\n,;]+/)
      .map((item) => item.trim())
      .filter(Boolean))];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setMessage("");
      const result = await fetchAllergensForAdmin();
      setDishes(result.dishes);
      setLabels(result.labels);
    } catch {
      setMessage("Erreur lors du chargement des allergènes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage("");
      const result = await saveAllergensToApi(dishes);
      setDishes(result.dishes);
      setMessage("Sauvegardé avec succès !");
      setTimeout(() => setMessage(""), 3000);
    } catch {
      setMessage("Erreur lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  const addDish = () => {
    const name = newDishName.trim();
    if (!name) return;

    const allergens = {};
    for (const label of labels) {
      allergens[label] = false;
    }

    setDishes((prev) => [
      ...prev,
      {
        id: `dish-${Date.now()}`,
        name,
        origin: newDishOrigin.trim(),
        suppliers: parseSuppliers(newDishSuppliers),
        allergens,
        updatedAt: new Date().toISOString(),
      },
    ]);
    setNewDishName("");
    setNewDishOrigin("");
    setNewDishSuppliers("");
  };

  const removeDish = (id) => {
    setDishes((prev) => prev.filter((d) => d.id !== id));
  };

  const moveDish = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= dishes.length) return;
    const updated = [...dishes];
    [updated[index], updated[target]] = [updated[target], updated[index]];
    setDishes(updated);
  };

  const toggleAllergen = (dishId, label) => {
    setDishes((prev) =>
      prev.map((d) =>
        d.id === dishId
          ? {
              ...d,
              allergens: { ...d.allergens, [label]: !d.allergens?.[label] },
            }
          : d,
      ),
    );
  };

  const updateDishName = (dishId, newName) => {
    setDishes((prev) =>
      prev.map((d) => (d.id === dishId ? { ...d, name: newName } : d)),
    );
  };

  const updateDishOrigin = (dishId, origin) => {
    setDishes((prev) =>
      prev.map((d) => (d.id === dishId ? { ...d, origin } : d)),
    );
  };

  const updateDishSuppliers = (dishId, suppliersText) => {
    setDishes((prev) =>
      prev.map((d) =>
        d.id === dishId
          ? { ...d, suppliers: parseSuppliers(suppliersText) }
          : d,
      ),
    );
  };

  if (loading) {
    return (
      <div className="allergens-admin">
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="allergens-admin">
      <div className="allergens-admin-header">
        <h2>Gestion des Allergènes</h2>
        <p className="allergens-admin-subtitle">
          Ajoutez vos plats et cochez les allergènes correspondants.
        </p>
      </div>

      {message && (
        <div
          className={`allergens-admin-message${message.includes("Erreur") ? " error" : " success"}`}
        >
          {message}
        </div>
      )}

      <div className="allergens-admin-add-row">
        <input
          type="text"
          className="allergens-admin-input"
          placeholder="Nom du plat..."
          value={newDishName}
          onChange={(e) => setNewDishName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addDish()}
        />
        <input
          type="text"
          className="allergens-admin-input"
          placeholder="Origine..."
          value={newDishOrigin}
          onChange={(e) => setNewDishOrigin(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addDish()}
        />
        <input
          type="text"
          className="allergens-admin-input allergens-admin-input-wide"
          placeholder="Fournisseurs, séparés par des virgules"
          value={newDishSuppliers}
          onChange={(e) => setNewDishSuppliers(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addDish()}
        />
        <button
          type="button"
          className="allergens-admin-btn allergens-admin-btn-add"
          onClick={addDish}
          disabled={!newDishName.trim()}
        >
          + Ajouter un plat
        </button>
      </div>

      {dishes.length === 0 ? (
        <p className="allergens-admin-empty">
          Aucun plat ajouté. Utilisez le champ ci-dessus pour commencer.
        </p>
      ) : (
        <div className="allergens-admin-table-wrap">
          <table className="allergens-admin-table">
            <thead>
              <tr>
                <th className="allergens-admin-corner">Plat</th>
                <th className="allergens-admin-text-header">Origine</th>
                <th className="allergens-admin-text-header">Fournisseurs</th>
                {labels.map((label, colIdx) => (
                  <th
                    key={label}
                    className="allergens-admin-col-header"
                    style={{
                      backgroundColor: ALLERGEN_COLORS[colIdx] || "#eee",
                    }}
                  >
                    <span className="allergens-admin-col-text">{label}</span>
                  </th>
                ))}
                <th className="allergens-admin-actions-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dishes.map((dish, index) => (
                <tr key={dish.id} className="allergens-admin-row">
                  <td className="allergens-admin-dish-name">
                    <input
                      type="text"
                      className="allergens-admin-name-input"
                      value={dish.name}
                      onChange={(e) => updateDishName(dish.id, e.target.value)}
                    />
                  </td>
                  <td className="allergens-admin-text-cell">
                    <input
                      type="text"
                      className="allergens-admin-name-input"
                      value={dish.origin || ""}
                      onChange={(e) => updateDishOrigin(dish.id, e.target.value)}
                    />
                  </td>
                  <td className="allergens-admin-text-cell">
                    <input
                      type="text"
                      className="allergens-admin-name-input allergens-admin-suppliers-input"
                      value={dish.suppliers?.join(", ") || ""}
                      onChange={(e) =>
                        updateDishSuppliers(dish.id, e.target.value)
                      }
                    />
                  </td>
                  {labels.map((label, colIdx) => (
                    <td
                      key={label}
                      className={`allergens-admin-cell${dish.allergens?.[label] ? " active" : ""}`}
                      style={{
                        backgroundColor: dish.allergens?.[label]
                          ? ALLERGEN_COLORS[colIdx] || "#eee"
                          : undefined,
                      }}
                      onClick={() => toggleAllergen(dish.id, label)}
                      title={`${label}: ${dish.allergens?.[label] ? "Oui" : "Non"}`}
                    >
                      {dish.allergens?.[label] ? "✕" : ""}
                    </td>
                  ))}
                  <td className="allergens-admin-actions">
                    <button
                      type="button"
                      className="allergens-admin-btn-sm"
                      onClick={() => moveDish(index, -1)}
                      disabled={index === 0}
                      title="Monter"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className="allergens-admin-btn-sm"
                      onClick={() => moveDish(index, 1)}
                      disabled={index === dishes.length - 1}
                      title="Descendre"
                    >
                      ▼
                    </button>
                    <button
                      type="button"
                      className="allergens-admin-btn-sm allergens-admin-btn-delete"
                      onClick={() => removeDish(dish.id)}
                      title="Supprimer"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="allergens-admin-save-row">
        <button
          type="button"
          className="allergens-admin-btn allergens-admin-btn-save"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Sauvegarde..." : "Sauvegarder"}
        </button>
      </div>
    </div>
  );
}

export default AllergensAdmin;
